import { useEffect } from 'react';
import { Product } from '@/mocks/products';

interface ReviewModalProps {
  product: Product;
  onClose: () => void;
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const starSize = size === 'lg' ? 'text-base' : 'text-xs';
  return (
    <div className={`flex items-center gap-0.5 ${starSize}`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= Math.floor(rating);
        const partial = !filled && star === Math.ceil(rating) && rating % 1 !== 0;
        return (
          <span key={star} className={filled || partial ? 'text-amber-400' : 'text-stone-200'}>
            <i className={filled ? 'ri-star-fill' : partial ? 'ri-star-half-fill' : 'ri-star-line'} />
          </span>
        );
      })}
    </div>
  );
}

function RatingBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-stone-500 w-8 flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-stone-400 w-6 text-right flex-shrink-0">{count}</span>
    </div>
  );
}

export default function ReviewModal({ product, onClose }: ReviewModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const ratingDist: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  product.reviews.forEach((r) => {
    ratingDist[r.rating] = (ratingDist[r.rating] || 0) + 1;
  });

  const avgDisplay = product.rating.toFixed(1);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-5 border-b border-stone-100">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{product.category}</p>
            <h2
              className="text-xl font-bold text-[#2c2420]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {product.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200 transition-colors cursor-pointer flex-shrink-0 mt-1"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {/* Rating summary */}
          <div className="p-6 flex gap-8 items-start border-b border-stone-100">
            <div className="text-center flex-shrink-0">
              <p
                className="text-5xl font-bold text-[#2c2420]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {avgDisplay}
              </p>
              <StarRating rating={product.rating} size="lg" />
              <p className="text-xs text-stone-400 mt-1">{product.reviewCount} reviews</p>
            </div>
            <div className="flex-1 space-y-2 pt-1">
              {[5, 4, 3, 2, 1].map((star) => (
                <RatingBar
                  key={star}
                  label={`${star}★`}
                  count={ratingDist[star]}
                  total={product.reviews.length}
                />
              ))}
            </div>
          </div>

          {/* Reviews list */}
          <div className="p-6 space-y-5">
            {product.reviews.map((review) => (
              <div key={review.id} className="pb-5 border-b border-stone-100 last:border-0 last:pb-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#f5f0ea] flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#8B6348]">{review.avatar}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[#2c2420]">{review.author}</span>
                      {review.verified && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                          <i className="ri-checkbox-circle-fill text-xs" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StarRating rating={review.rating} />
                      <span className="text-[10px] text-stone-400">{review.date}</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm font-semibold text-[#2c2420] mb-1">{review.title}</p>
                <p className="text-sm text-stone-500 leading-relaxed">{review.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-stone-100 bg-[#faf8f5]">
          <p className="text-center text-xs text-stone-400">
            Showing {product.reviews.length} of {product.reviewCount} reviews
          </p>
        </div>
      </div>
    </div>
  );
}
